import React, { useState, useEffect } from 'react';
import {
  Ticket, RefreshCw, FileCheck, Calendar, Database, Server
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useSecurity } from '../security/SecurityContext';
import { API, authFetch } from '../lib/api';
import StatCard from './_shared/StatCard';

// ─── License Operator Dashboard (non-super-admin operators) ───────────────
export default function LicenseOperatorDashboard() {
  const { user } = useAuth();
  const { mssqlConnected, chainHeight } = useSecurity();
  const [stats, setStats] = useState({ totalLicenses: 0, activeLicenses: 0, expiringSoon: 0, totalRevenue: 0 });
  const [reminders, setReminders] = useState([]);
  const [recentLicenses, setRecentLicenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      authFetch(`${API}/license/all`).then(r => r.json()).catch(() => []),
      authFetch(`${API}/license/reminders`).then(r => r.json()).catch(() => [])
    ]).then(([allLicenses, expiring]) => {
      const active = (allLicenses || []).filter(l => l.status === 'Active');
      const revenue = (allLicenses || []).reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
      setStats({
        totalLicenses: (allLicenses || []).length,
        activeLicenses: active.length,
        expiringSoon: (expiring || []).length,
        totalRevenue: revenue
      });
      setReminders(expiring || []);
      setRecentLicenses((allLicenses || []).slice(0, 8));
      setLoading(false);
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1200 }}>
      {/* Welcome */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: '#f8fafc', padding: 28, border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 56, height: 56, background: 'rgba(59,130,246,0.15)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(59,130,246,0.4)' }}>
            <Ticket size={26} color="#60a5fa" />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#60a5fa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>License Operator Console</div>
            <div style={{ fontSize: 22, fontWeight: 900 }}>Welcome, {user?.username || 'Operator'}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>You can issue, renew, and verify licenses. Key Settings and Licensor Console are reserved for Super Admin.</div>
          </div>
        </div>
      </div>

      {/* Stats focused on operator concerns */}
      <div className="stat-grid">
        <StatCard label="Active Licenses" value={loading ? '…' : stats.activeLicenses} sub={`of ${stats.totalLicenses} total`} trend="Live" icon={<Ticket size={16} color="#3b82f6" />} />
        <StatCard label="Expiring (30d)" value={loading ? '…' : stats.expiringSoon} sub="Need renewal action" trend={stats.expiringSoon > 0 ? 'Action' : 'Clear'} icon={<Calendar size={16} color={stats.expiringSoon > 0 ? '#f59e0b' : '#10b981'} />} />
        <StatCard label="Total Revenue" value={loading ? '…' : `₹ ${stats.totalRevenue.toLocaleString()}`} sub="Cumulative" trend="Audited" icon={<Database size={16} color="#10b981" />} />
        <StatCard label="DB Link" value={mssqlConnected ? 'LIVE' : 'FILE MODE'} sub="RC26masterDB" trend={mssqlConnected ? 'Sync' : 'Degraded'} icon={<Server size={16} color={mssqlConnected ? '#10b981' : '#f59e0b'} />} />
      </div>

      {/* Quick actions */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Quick Actions</span>
          <span className="card-badge">OPERATOR TOOLS</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, padding: 16 }}>
          {[
            { icon: Ticket,    title: 'Generate License',    desc: 'Issue a new DNA-signed license to a client',          tab: 'licenses' },
            { icon: RefreshCw, title: 'Renew License',       desc: 'Extend an existing license expiry',                    tab: 'licenses' },
            { icon: FileCheck, title: 'View Audit Trail',    desc: 'Blockchain log of all licensing events',               tab: 'audit' },
          ].map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.title} style={{ padding: 16, border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, background: '#3b82f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={16} color="#fff" />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{a.title}</div>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>{a.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Renewal alerts */}
      {reminders.length > 0 && (
        <div className="alert-banner amber">
          <Calendar size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>Critical Renewal Alerts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {reminders.slice(0, 5).map((r) => (
                <div key={r.clientId || r.clientName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 6, padding: '6px 12px', border: '1px solid #fde68a' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{r.clientName}</span>
                  <span style={{ fontSize: 10, color: '#64748b' }}>Expires {new Date(r.expiryDate).toLocaleDateString()}</span>
                  <span className={`badge ${r.daysLeft <= 7 ? 'badge-red' : 'badge-amber'}`}>
                    {r.daysLeft === 1 ? 'Tomorrow' : `${r.daysLeft}d left`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent licenses */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recent Licenses Issued</span>
          <span className="card-badge">TOP 8</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr><th>Client</th><th>Tier</th><th>Amount</th><th>Issued</th><th>Expiry</th><th>Status</th></tr></thead>
            <tbody>
              {recentLicenses.map((l) => (
                <tr key={l.licenseId || l.clientId || l.LicenseId || l.ClientId}>
                  <td>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{l.clientName || '—'}</div>
                    <div style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'monospace' }}>{l.clientId}</div>
                  </td>
                  <td><span className="badge badge-blue">{l.tier}</span></td>
                  <td style={{ fontWeight: 700 }}>₹ {(Number(l.amount) || 0).toLocaleString()}</td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{l.issuedDate ? new Date(l.issuedDate).toLocaleDateString() : '—'}</td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{l.expiryDate ? new Date(l.expiryDate).toLocaleDateString() : '—'}</td>
                  <td><span className={`badge ${l.status === 'Active' ? 'badge-green' : 'badge-amber'}`}>{l.status}</span></td>
                </tr>
              ))}
              {recentLicenses.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>No licenses yet. Use the License Engine tab to issue one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid #e2e8f0', fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <span>Operator Role</span><span>Chain Height: {chainHeight}</span><span>Limited Privilege</span>
        </div>
        <span>© MSN-CLIQ vEritEqcLiQ Retail ERP</span>
      </footer>
    </div>
  );
}
